import React, { useState, useMemo, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Tag,
  Space,
  Card,
  Empty,
  Typography,
  Tooltip,
  App,
  DatePicker,
  Descriptions,
  Divider,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ExclamationCircleOutlined,
  SendOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import {
  useGetQuotationsQuery,
  useCreateQuotationMutation,
  useUpdateQuotationMutation,
  useDeleteQuotationMutation,
  useUpdateQuotationStatusMutation,
  useLazyGetQuotationPdfQuery,
} from '../../services/quotationsApi';
import { useGetProductsQuery } from '../../services/productsApi';
import type {
  Quotation,
  QuotationStatus,
  QuotationItem,
  CreateQuotationRequest,
  UpdateQuotationRequest,
  CreateQuotationItemRequest,
} from '../../types/inventory';
import type { Product } from '../../types/products';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// =====================================================
// Status Helpers
// =====================================================

const STATUS_TAG_COLORS: Record<QuotationStatus, string> = {
  Draft: 'default',
  Sent: 'blue',
  Accepted: 'green',
  Rejected: 'red',
  Expired: 'gray',
};

const STATUS_LABELS: Record<QuotationStatus, string> = {
  Draft: 'Borrador',
  Sent: 'Enviada',
  Accepted: 'Aceptada',
  Rejected: 'Rechazada',
  Expired: 'Expirada',
};

// =====================================================
// Item Row Interface for the dynamic form
// =====================================================

interface ItemRow {
  key: string;
  description: string;
  productId?: string;
  quantity: number;
  unitPrice: number;
  notes?: string;
}

let itemKeyCounter = 0;
const generateItemKey = () => `item_${++itemKeyCounter}_${Date.now()}`;

// =====================================================
// Main QuotationsTab Component
// =====================================================

const QuotationsTab: React.FC = () => {
  const { isMobile } = useResponsive();
  const { message, modal } = App.useApp();
  const { currencySymbol } = useCurrency();
  const { formatInTimeZone } = useRestaurantTimeZone();

  // API hooks
  const { data: quotations = [], isLoading } = useGetQuotationsQuery();
  const { data: products = [] } = useGetProductsQuery({ availableOnly: false });
  const [createQuotation, { isLoading: creating }] = useCreateQuotationMutation();
  const [updateQuotation, { isLoading: updating }] = useUpdateQuotationMutation();
  const [deleteQuotation] = useDeleteQuotationMutation();
  const [updateQuotationStatus, { isLoading: updatingStatus }] = useUpdateQuotationStatusMutation();
  const [triggerGetPdf] = useLazyGetQuotationPdfQuery();

  // Local state
  const [statusFilter, setStatusFilter] = useState<QuotationStatus | 'all'>('all');
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editingQuotation, setEditingQuotation] = useState<Quotation | null>(null);
  const [viewingQuotation, setViewingQuotation] = useState<Quotation | null>(null);
  const [form] = Form.useForm();
  const [items, setItems] = useState<ItemRow[]>([]);

  // Filtered quotations
  const filteredQuotations = useMemo(() => {
    if (statusFilter === 'all') return quotations;
    return quotations.filter((q) => q.status === statusFilter);
  }, [quotations, statusFilter]);

  // =====================================================
  // Items management
  // =====================================================

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        key: generateItemKey(),
        description: '',
        quantity: 1,
        unitPrice: 0,
      },
    ]);
  }, []);

  const handleRemoveItem = useCallback((key: string) => {
    setItems((prev) => prev.filter((item) => item.key !== key));
  }, []);

  const handleItemChange = useCallback(
    (key: string, field: keyof ItemRow, value: string | number | undefined) => {
      setItems((prev) =>
        prev.map((item) => {
          if (item.key !== key) return item;
          return { ...item, [field]: value };
        })
      );
    },
    []
  );

  const handleProductSelect = useCallback(
    (key: string, productId: string | undefined) => {
      if (!productId) {
        handleItemChange(key, 'productId', undefined);
        return;
      }
      const product = products.find((p: Product) => p.id === productId);
      if (product) {
        setItems((prev) =>
          prev.map((item) => {
            if (item.key !== key) return item;
            return {
              ...item,
              productId,
              description: product.name,
              unitPrice: product.price,
            };
          })
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products]
  );

  // Calculated totals - prices already include tax (IVA)
  // Total = sum of item totals, Subtotal = Total / (1 + taxRate/100), Tax = Total - Subtotal
  const currentTaxRate = Form.useWatch('taxRate', form) ?? 16;

  const calculatedTotal = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0),
    [items]
  );

  const calculatedSubtotal = calculatedTotal / (1 + currentTaxRate / 100);
  const calculatedTax = calculatedTotal - calculatedSubtotal;

  // =====================================================
  // Form Modal handlers
  // =====================================================

  const handleOpenCreate = () => {
    setEditingQuotation(null);
    form.resetFields();
    form.setFieldsValue({ taxRate: 16 });
    setItems([
      { key: generateItemKey(), description: '', quantity: 1, unitPrice: 0 },
    ]);
    setFormModalOpen(true);
  };

  const handleOpenEdit = (quotation: Quotation) => {
    setEditingQuotation(quotation);
    form.setFieldsValue({
      clientName: quotation.clientName,
      clientEmail: quotation.clientEmail,
      clientPhone: quotation.clientPhone,
      clientAddress: quotation.clientAddress,
      taxRate: quotation.taxRate,
      validUntil: quotation.validUntil ? dayjs(quotation.validUntil) : undefined,
      notes: quotation.notes,
    });
    setItems(
      quotation.items.map((qi) => ({
        key: generateItemKey(),
        description: qi.description,
        productId: qi.productId,
        quantity: qi.quantity,
        unitPrice: qi.unitPrice,
        notes: qi.notes,
      }))
    );
    setFormModalOpen(true);
  };

  const handleCloseFormModal = () => {
    setFormModalOpen(false);
    setEditingQuotation(null);
    form.resetFields();
    setItems([]);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      // Validate items
      if (items.length === 0) {
        message.error('Debe agregar al menos un articulo');
        return;
      }

      const invalidItems = items.filter(
        (item) => !item.description.trim() || item.quantity <= 0 || item.unitPrice < 0
      );
      if (invalidItems.length > 0) {
        message.error('Todos los articulos deben tener descripcion, cantidad mayor a 0 y precio valido');
        return;
      }

      const itemsPayload: CreateQuotationItemRequest[] = items.map((item) => ({
        description: item.description,
        productId: item.productId || undefined,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        notes: item.notes || undefined,
      }));

      if (editingQuotation) {
        const updateData: UpdateQuotationRequest = {
          clientName: values.clientName,
          clientEmail: values.clientEmail || undefined,
          clientPhone: values.clientPhone || undefined,
          clientAddress: values.clientAddress || undefined,
          notes: values.notes || undefined,
          taxRate: values.taxRate,
          validUntil: values.validUntil ? values.validUntil.toISOString() : undefined,
          items: itemsPayload,
        };
        await updateQuotation({ id: editingQuotation.id, data: updateData }).unwrap();
        message.success('Cotizacion actualizada correctamente');
      } else {
        const createData: CreateQuotationRequest = {
          clientName: values.clientName,
          clientEmail: values.clientEmail || undefined,
          clientPhone: values.clientPhone || undefined,
          clientAddress: values.clientAddress || undefined,
          notes: values.notes || undefined,
          taxRate: values.taxRate,
          validUntil: values.validUntil ? values.validUntil.toISOString() : undefined,
          items: itemsPayload,
        };
        await createQuotation(createData).unwrap();
        message.success('Cotizacion creada correctamente');
      }

      handleCloseFormModal();
    } catch (error: any) {
      if (error?.errorFields) return;
      message.error(error?.data?.message || 'Error al guardar la cotizacion');
    }
  };

  // =====================================================
  // Delete handler
  // =====================================================

  const handleDelete = (quotation: Quotation) => {
    modal.confirm({
      title: 'Eliminar Cotizacion',
      icon: <ExclamationCircleOutlined />,
      content: `¿Esta seguro de eliminar la cotizacion #${quotation.quotationNumber} para "${quotation.clientName}"?`,
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteQuotation(quotation.id).unwrap();
          message.success('Cotizacion eliminada correctamente');
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al eliminar la cotizacion');
        }
      },
    });
  };

  // =====================================================
  // Detail Modal handlers
  // =====================================================

  const handleViewDetail = (quotation: Quotation) => {
    setViewingQuotation(quotation);
    setDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setViewingQuotation(null);
  };

  // =====================================================
  // Status transition handler
  // =====================================================

  const handleStatusChange = async (quotation: Quotation, newStatus: QuotationStatus) => {
    const statusLabels: Record<string, string> = {
      Sent: 'enviar',
      Accepted: 'aceptar',
      Rejected: 'rechazar',
    };

    modal.confirm({
      title: `Cambiar Estado`,
      icon: <ExclamationCircleOutlined />,
      content: `¿Esta seguro de ${statusLabels[newStatus] || 'cambiar'} la cotizacion #${quotation.quotationNumber}? Esta accion no se puede revertir.`,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await updateQuotationStatus({ id: quotation.id, status: newStatus }).unwrap();
          message.success(`Cotizacion ${STATUS_LABELS[newStatus].toLowerCase()} correctamente`);
          // Refresh detail modal if viewing
          if (viewingQuotation?.id === quotation.id) {
            setViewingQuotation(null);
            setDetailModalOpen(false);
          }
        } catch (error: any) {
          message.error(error?.data?.message || 'Error al cambiar el estado');
        }
      },
    });
  };

  // =====================================================
  // PDF Download handler
  // =====================================================

  const handleDownloadPdf = async (quotation: Quotation) => {
    try {
      message.loading({ content: 'Generando PDF...', key: 'pdf-download' });
      const result = await triggerGetPdf(quotation.id).unwrap();
      const url = URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cotizacion_${quotation.quotationNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success({ content: 'PDF descargado', key: 'pdf-download' });
    } catch {
      message.error({ content: 'Error al generar el PDF', key: 'pdf-download' });
    }
  };

  // =====================================================
  // Table columns
  // =====================================================

  const columns: ColumnsType<Quotation> = [
    {
      title: '# Cotizacion',
      dataIndex: 'quotationNumber',
      key: 'quotationNumber',
      width: 120,
      render: (num: number) => (
        <Text strong style={{ fontFamily: 'monospace' }}>
          #{num}
        </Text>
      ),
    },
    {
      title: 'Cliente',
      dataIndex: 'clientName',
      key: 'clientName',
      ellipsis: true,
      render: (name: string) => <Text strong>{name}</Text>,
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => (
        <Text style={{ fontSize: 12 }}>{formatInTimeZone(date, 'DD/MM/YYYY')}</Text>
      ),
    },
    {
      title: 'Valida Hasta',
      dataIndex: 'validUntil',
      key: 'validUntil',
      width: 120,
      render: (date: string | undefined) =>
        date ? (
          <Text style={{ fontSize: 12 }}>{formatInTimeZone(date, 'DD/MM/YYYY')}</Text>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      align: 'center',
      render: (status: QuotationStatus) => (
        <Tag color={STATUS_TAG_COLORS[status]}>{STATUS_LABELS[status]}</Tag>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (total: number) => (
        <Text strong style={{ color: 'var(--color-success-text)' }}>
          {currencySymbol} {total.toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 200,
      align: 'center',
      render: (_: unknown, record: Quotation) => (
        <Space size="small">
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="Descargar PDF">
            <Button
              type="text"
              size="small"
              icon={<FilePdfOutlined />}
              onClick={() => handleDownloadPdf(record)}
            />
          </Tooltip>
          {record.status === 'Draft' && (
            <>
              <Tooltip title="Editar">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleOpenEdit(record)}
                />
              </Tooltip>
              <Tooltip title="Eliminar">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  // =====================================================
  // Detail Modal: items table columns
  // =====================================================

  const detailItemColumns: ColumnsType<QuotationItem> = [
    {
      title: 'Descripcion',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Cantidad',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 90,
      align: 'center',
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 130,
      align: 'right',
      render: (price: number) => `${currencySymbol} ${price.toFixed(2)}`,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (total: number) => (
        <Text strong>{currencySymbol} {total.toFixed(2)}</Text>
      ),
    },
  ];

  // =====================================================
  // Status action buttons for detail modal
  // =====================================================

  const renderStatusActions = (quotation: Quotation) => {
    switch (quotation.status) {
      case 'Draft':
        return (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => handleStatusChange(quotation, 'Sent')}
            loading={updatingStatus}
          >
            Enviar
          </Button>
        );
      case 'Sent':
        return (
          <Space>
            <Button
              type="primary"
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
              icon={<CheckCircleOutlined />}
              onClick={() => handleStatusChange(quotation, 'Accepted')}
              loading={updatingStatus}
            >
              Aceptar
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => handleStatusChange(quotation, 'Rejected')}
              loading={updatingStatus}
            >
              Rechazar
            </Button>
          </Space>
        );
      default:
        return null;
    }
  };

  // =====================================================
  // Mobile card renderer
  // =====================================================

  const renderMobileCard = (quotation: Quotation) => (
    <Card
      key={quotation.id}
      style={{ marginBottom: 12 }}
      size="small"
      actions={[
        <Button
          type="text"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetail(quotation)}
          size="small"
          key="view"
        >
          Ver
        </Button>,
        <Button
          type="text"
          icon={<FilePdfOutlined />}
          onClick={() => handleDownloadPdf(quotation)}
          size="small"
          key="pdf"
        >
          PDF
        </Button>,
        ...(quotation.status === 'Draft'
          ? [
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleOpenEdit(quotation)}
                size="small"
                key="edit"
              >
                Editar
              </Button>,
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(quotation)}
                size="small"
                key="delete"
              >
                Eliminar
              </Button>,
            ]
          : []),
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <FileTextOutlined />
            <Text strong>#{quotation.quotationNumber}</Text>
          </Space>
          <Tag color={STATUS_TAG_COLORS[quotation.status]}>
            {STATUS_LABELS[quotation.status]}
          </Tag>
        </div>

        <div style={{ fontSize: 13 }}>
          <UserOutlined style={{ marginRight: 6, color: 'var(--color-neutral-text)' }} />
          {quotation.clientName}
        </div>

        <div style={{ fontSize: 13, color: 'var(--color-neutral-text)' }}>
          {formatInTimeZone(quotation.createdAt, 'DD/MM/YYYY')}
          {quotation.validUntil && (
            <> | Valida hasta: {formatInTimeZone(quotation.validUntil, 'DD/MM/YYYY')}</>
          )}
        </div>

        <div style={{ fontSize: 14 }}>
          <Text strong style={{ color: 'var(--color-success-text)' }}>
            {currencySymbol} {quotation.total.toFixed(2)}
          </Text>
        </div>
      </Space>
    </Card>
  );

  // =====================================================
  // Render
  // =====================================================

  return (
    <>
      <div style={{ padding: isMobile ? 0 : '16px 0' }}>
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Select
            value={statusFilter}
            onChange={(val) => setStatusFilter(val)}
            style={{ width: isMobile ? 150 : 180 }}
          >
            <Option value="all">Todos los Estados</Option>
            <Option value="Draft">Borrador</Option>
            <Option value="Sent">Enviada</Option>
            <Option value="Accepted">Aceptada</Option>
            <Option value="Rejected">Rechazada</Option>
            <Option value="Expired">Expirada</Option>
          </Select>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreate}
          >
            Nueva Cotizacion
          </Button>
        </div>

        {/* Content: Table or Mobile Cards */}
        {isMobile ? (
          isLoading ? (
            <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
          ) : filteredQuotations.length > 0 ? (
            <div style={{ maxHeight: 'calc(100vh - 300px)', overflowY: 'auto' }}>
              {filteredQuotations.map((q) => renderMobileCard(q))}
            </div>
          ) : (
            <Empty
              description="No hay cotizaciones registradas"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              style={{ padding: 40 }}
            />
          )
        ) : (
          <Table
            columns={columns}
            dataSource={filteredQuotations}
            loading={isLoading}
            rowKey="id"
            size="middle"
            scroll={{ x: 800 }}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} cotizaciones`,
            }}
            locale={{
              emptyText: (
                <Empty
                  description="No hay cotizaciones registradas"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ),
            }}
          />
        )}
      </div>

      {/* =====================================================
          Create / Edit Quotation Modal
          ===================================================== */}
      <Modal
        title={editingQuotation ? 'Editar Cotizacion' : 'Nueva Cotizacion'}
        open={formModalOpen}
        onOk={handleSave}
        onCancel={handleCloseFormModal}
        confirmLoading={creating || updating}
        okText={editingQuotation ? 'Guardar' : 'Crear'}
        cancelText="Cancelar"
        width={isMobile ? '98%' : 800}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 16 }}
        >
          {/* Client Information */}
          <Divider orientation="left" style={{ fontSize: 14, marginTop: 0 }}>
            Informacion del Cliente
          </Divider>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="clientName"
                label="Nombre del Cliente"
                rules={[{ required: true, message: 'Ingrese el nombre del cliente' }]}
              >
                <Input placeholder="Nombre del cliente" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="clientEmail"
                label="Email"
                rules={[{ type: 'email', message: 'Ingrese un email valido' }]}
              >
                <Input placeholder="Email (opcional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="clientPhone" label="Telefono">
                <Input placeholder="Telefono (opcional)" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="validUntil" label="Valida Hasta">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  placeholder="Fecha de vencimiento"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="clientAddress" label="Direccion">
            <TextArea rows={2} placeholder="Direccion del cliente (opcional)" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="taxRate"
                label="IVA %"
                rules={[{ required: true, message: 'Ingrese el porcentaje de IVA' }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={1}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="16"
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notas">
            <TextArea rows={2} placeholder="Notas adicionales (opcional)" />
          </Form.Item>

          {/* Items */}
          <Divider orientation="left" style={{ fontSize: 14 }}>
            Articulos
          </Divider>

          {items.map((item, index) => (
            <Card
              key={item.key}
              size="small"
              style={{ marginBottom: 12 }}
              title={
                <Text style={{ fontSize: 13 }}>Articulo {index + 1}</Text>
              }
              extra={
                items.length > 1 && (
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<MinusCircleOutlined />}
                    onClick={() => handleRemoveItem(item.key)}
                  >
                    {!isMobile && 'Eliminar'}
                  </Button>
                )
              }
            >
              <Row gutter={12}>
                <Col xs={24} sm={8}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Producto (opcional)
                    </Text>
                    <Select
                      placeholder="Seleccionar producto"
                      value={item.productId || undefined}
                      onChange={(val) => handleProductSelect(item.key, val)}
                      allowClear
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.children as unknown as string)
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                      style={{ width: '100%' }}
                      size={isMobile ? 'middle' : 'middle'}
                    >
                      {products.map((p: Product) => (
                        <Option key={p.id} value={p.id}>
                          {p.name}
                        </Option>
                      ))}
                    </Select>
                  </div>
                </Col>
                <Col xs={24} sm={16}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Descripcion *
                    </Text>
                    <Input
                      placeholder="Descripcion del articulo"
                      value={item.description}
                      onChange={(e) => handleItemChange(item.key, 'description', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col xs={8} sm={6}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Cantidad *
                    </Text>
                    <InputNumber
                      min={0.01}
                      step={1}
                      precision={2}
                      value={item.quantity}
                      onChange={(val) => handleItemChange(item.key, 'quantity', val ?? 0)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col xs={8} sm={6}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Precio Unitario *
                    </Text>
                    <InputNumber
                      min={0}
                      step={1}
                      precision={2}
                      prefix={currencySymbol}
                      value={item.unitPrice}
                      onChange={(val) => handleItemChange(item.key, 'unitPrice', val ?? 0)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </Col>
                <Col xs={8} sm={6}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Subtotal
                    </Text>
                    <Text strong style={{ lineHeight: '32px', display: 'block' }}>
                      {currencySymbol} {(item.quantity * item.unitPrice).toFixed(2)}
                    </Text>
                  </div>
                </Col>
                <Col xs={24} sm={6}>
                  <div style={{ marginBottom: 12 }}>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                      Notas
                    </Text>
                    <Input
                      placeholder="Notas"
                      value={item.notes || ''}
                      onChange={(e) => handleItemChange(item.key, 'notes', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </Card>
          ))}

          <Button
            type="dashed"
            onClick={handleAddItem}
            block
            icon={<PlusOutlined />}
            style={{ marginBottom: 16 }}
          >
            Agregar Articulo
          </Button>

          {/* Totals Summary */}
          {items.length > 0 && (
            <div
              style={{
                padding: '12px 16px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 8,
                border: '1px solid var(--border-color)',
              }}
            >
              <Row justify="end">
                <Col xs={24} sm={10}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary">Subtotal:</Text>
                    <Text>{currencySymbol} {calculatedSubtotal.toFixed(2)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary">IVA ({currentTaxRate}%):</Text>
                    <Text>{currencySymbol} {calculatedTax.toFixed(2)}</Text>
                  </div>
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: 16 }}>Total:</Text>
                    <Text strong style={{ fontSize: 16, color: 'var(--color-success-text)' }}>
                      {currencySymbol} {calculatedTotal.toFixed(2)}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </Form>
      </Modal>

      {/* =====================================================
          View Detail Modal
          ===================================================== */}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            <span>Cotizacion #{viewingQuotation?.quotationNumber}</span>
          </Space>
        }
        open={detailModalOpen}
        onCancel={handleCloseDetailModal}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {viewingQuotation && renderStatusActions(viewingQuotation)}
              {viewingQuotation && (
                <Button
                  icon={<FilePdfOutlined />}
                  onClick={() => handleDownloadPdf(viewingQuotation)}
                >
                  Descargar PDF
                </Button>
              )}
            </div>
            <Button onClick={handleCloseDetailModal}>Cerrar</Button>
          </div>
        }
        width={isMobile ? '98%' : 700}
        style={{ top: isMobile ? 10 : 20 }}
        destroyOnClose
      >
        {viewingQuotation && (
          <div style={{ padding: isMobile ? '8px 0' : '16px 0' }}>
            {/* Status */}
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Tag
                color={STATUS_TAG_COLORS[viewingQuotation.status]}
                style={{ fontSize: 14, padding: '4px 12px' }}
              >
                {STATUS_LABELS[viewingQuotation.status]}
              </Tag>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Creada por: {viewingQuotation.createdByUserName}
              </Text>
            </div>

            {/* Client Info */}
            <Descriptions
              bordered
              size="small"
              column={isMobile ? 1 : 2}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label={<><UserOutlined style={{ marginRight: 4 }} /> Cliente</>}>
                <Text strong>{viewingQuotation.clientName}</Text>
              </Descriptions.Item>
              {viewingQuotation.clientEmail && (
                <Descriptions.Item label={<><MailOutlined style={{ marginRight: 4 }} /> Email</>}>
                  {viewingQuotation.clientEmail}
                </Descriptions.Item>
              )}
              {viewingQuotation.clientPhone && (
                <Descriptions.Item label={<><PhoneOutlined style={{ marginRight: 4 }} /> Telefono</>}>
                  {viewingQuotation.clientPhone}
                </Descriptions.Item>
              )}
              {viewingQuotation.clientAddress && (
                <Descriptions.Item
                  label={<><EnvironmentOutlined style={{ marginRight: 4 }} /> Direccion</>}
                  span={isMobile ? 1 : 2}
                >
                  {viewingQuotation.clientAddress}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Fecha">
                {formatInTimeZone(viewingQuotation.createdAt, 'DD/MM/YYYY')}
              </Descriptions.Item>
              {viewingQuotation.validUntil && (
                <Descriptions.Item label="Valida Hasta">
                  {formatInTimeZone(viewingQuotation.validUntil, 'DD/MM/YYYY')}
                </Descriptions.Item>
              )}
              {viewingQuotation.notes && (
                <Descriptions.Item label="Notas" span={isMobile ? 1 : 2}>
                  {viewingQuotation.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Items Table */}
            <Divider orientation="left" style={{ fontSize: 14 }}>
              Articulos
            </Divider>

            <Table
              columns={detailItemColumns}
              dataSource={viewingQuotation.items}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ x: isMobile ? 500 : undefined }}
              locale={{
                emptyText: (
                  <Empty
                    description="No hay articulos"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />

            {/* Totals */}
            <div
              style={{
                marginTop: 16,
                padding: '12px 16px',
                backgroundColor: 'var(--bg-success-light)',
                borderRadius: 8,
                border: '1px solid var(--border-success)',
              }}
            >
              <Row justify="end">
                <Col xs={24} sm={10}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary">Subtotal:</Text>
                    <Text>{currencySymbol} {viewingQuotation.subtotal.toFixed(2)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <Text type="secondary">IVA ({viewingQuotation.taxRate}%):</Text>
                    <Text>{currencySymbol} {viewingQuotation.tax.toFixed(2)}</Text>
                  </div>
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Text strong style={{ fontSize: 16 }}>Total:</Text>
                    <Text strong style={{ fontSize: 16, color: 'var(--color-success-text)' }}>
                      {currencySymbol} {viewingQuotation.total.toFixed(2)}
                    </Text>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default QuotationsTab;
